/**
 * Page-context registry — framework-agnostic singleton living OUTSIDE React.
 *
 * Pages declare "what is on screen" (page id, entity, form schema, available
 * actions) so the copilot can send an accurate page_context to the backend
 * and target its actions. A stack is used so nested registrations (page +
 * modal form, for instance) resolve to the most recently registered context.
 */

import { useEffect } from 'react';

import type { PageContext } from './types';

const stack: PageContext[] = [];

/**
 * Registers a page context and returns its unregister function.
 * Safe to call multiple times; unregister is idempotent.
 */
export function registerPage(ctx: PageContext): () => void {
  stack.push(ctx);
  let active = true;
  return () => {
    if (!active) return;
    active = false;
    const index = stack.indexOf(ctx);
    if (index !== -1) stack.splice(index, 1);
  };
}

/** Returns the most recently registered page context, or null. */
export function getCurrent(): PageContext | null {
  return stack.length > 0 ? stack[stack.length - 1] : null;
}

/**
 * React hook: registers `ctx` on mount and unregisters on unmount.
 * The dependency is the serialized context, so passing an inline object
 * literal does not cause register/unregister churn on every render.
 *
 * Usage in a page component:
 *   useCopilotPage({ pageId: 'crm', actions: ['create_lead'] });
 */
export function useCopilotPage(ctx: PageContext): void {
  const key = JSON.stringify(ctx);
  useEffect(() => {
    return registerPage(ctx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
